/**
 * TenantThemeProvider Component
 *
 * Loads tenant-specific theme overrides from the API and merges them with global design tokens.
 * Injects CSS custom properties at runtime for multi-tenant branding support.
 *
 * Features:
 * - Fetches tenant theme from GET /admin/theme endpoint
 * - Deep merges tenant overrides with global design-tokens.json
 * - Generates and injects CSS custom properties into <head>
 * - Provides ThemeContext for components to access theme data
 * - Supports preview mode via preview_token query parameter
 * - Multi-language error messages (DE/EN/ES)
 * - Performance optimizations: memoization, debounced DOM updates
 *
 * @example
 * <TenantThemeProvider>
 *   <App />
 * </TenantThemeProvider>
 */

"use client";

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getUser as getAuthUser, getUserRoles } from '../services/auth';
import { rolesResultFor } from '../hooks/useUserRoles';
import { canUseConsole } from './common/RoleRoute';
import { getApiOrigin } from '../utils/api-base-url';

// Import global design tokens as fallback
import globalDesignTokens from '@/theme/design-tokens.json';

// ============================================================================
// TypeScript Interfaces (matching API ThemeSettingsResponse)
// ============================================================================

export interface DesignTokens {
  colors?: Record<string, any>;
  typography?: Record<string, any>;
  spacing?: Record<string, any>;
  borderRadius?: Record<string, any>;
  shadows?: Record<string, any>;
  animation?: Record<string, any>;
  glassmorphism?: Record<string, any>;
  components?: Record<string, any>;
  breakpoints?: Record<string, any>;
  zIndex?: Record<string, any>;
}

export interface TenantThemeSettings extends DesignTokens {
  id?: string;
  tenant_id?: string;
  theme_version?: string;
  custom_css?: string;
  is_active?: boolean;
  is_published?: boolean;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ThemeContextValue {
  theme: DesignTokens;
  tenantTheme: TenantThemeSettings | null;
  isLoading: boolean;
  error: string | null;
  refetchTheme: () => Promise<void>;
}

// ============================================================================
// Theme Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTenantTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTenantTheme must be used within TenantThemeProvider');
  }
  return context;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Deep merge two objects, with override taking precedence
 */
function deepMerge<T extends Record<string, any>>(target: T, override: Partial<T>): T {
  const result = { ...target };

  for (const key in override) {
    if (Object.prototype.hasOwnProperty.call(override, key)) {
      const overrideValue = override[key];
      const targetValue = result[key];

      if (
        overrideValue !== null &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        targetValue !== null &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        // Recursively merge nested objects
        result[key] = deepMerge(targetValue, overrideValue);
      } else {
        // Override primitive values and arrays
        result[key] = overrideValue as any;
      }
    }
  }

  return result;
}

/**
 * Flatten nested design token object to CSS custom property declarations
 * Example: { colors: { primary: { 500: "#1A6B5A" } } } → "--color-primary-500: #1A6B5A"
 */
function generateCSSCustomProperties(tokens: DesignTokens, _prefix: string = ''): string {
  let css = '';

  const processValue = (value: any, path: string[]): void => {
    if (value === null || value === undefined) {
      return;
    }

    // Handle primitive values (strings, numbers, booleans)
    if (typeof value !== 'object' || Array.isArray(value)) {
      const propertyName = `--${path.join('-')}`;
      const propertyValue = Array.isArray(value) ? value.join(', ') : String(value);
      css += `  ${propertyName}: ${propertyValue};\n`;
      return;
    }

    // Recursively process nested objects
    for (const [key, val] of Object.entries(value)) {
      // Convert camelCase to kebab-case for CSS
      const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      processValue(val, [...path, kebabKey]);
    }
  };

  // Process each top-level category
  for (const [category, value] of Object.entries(tokens)) {
    if (category === 'custom_css') continue; // Skip custom CSS field

    // Convert category names to kebab-case
    const kebabCategory = category.replace(/([A-Z])/g, '-$1').toLowerCase();
    processValue(value, [kebabCategory]);
  }

  return css;
}

/**
 * Inject CSS custom properties into <head> via <style> tag
 */
function injectCustomProperties(css: string, customCss?: string): void {
  // Remove existing theme style tag if present
  const existingStyle = document.getElementById('tenant-theme-custom-properties');
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create new style tag
  const styleTag = document.createElement('style');
  styleTag.id = 'tenant-theme-custom-properties';
  styleTag.textContent = `:root {\n${css}}\n\n${customCss || ''}`;

  // Insert at the end of <head> to ensure it has high specificity
  document.head.appendChild(styleTag);
}

/**
 * Debounce function for performance optimization
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);
  };
}

// ============================================================================
// TenantThemeProvider Component
// ============================================================================

export interface TenantThemeProviderProps {
  children: React.ReactNode;
  /**
   * Optional tenant ID override (defaults to tenant from context/auth)
   */
  tenantId?: string;
  /**
   * Optional preview token for testing unpublished themes
   */
  previewToken?: string;
  /**
   * API base URL (defaults to VITE_API_URL env var). Supports both:
   * - https://api.agentic360.de
   * - https://api.agentic360.de/api/v1
   */
  apiBaseUrl?: string;
}

function normalizeApiBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/api/v1') ? trimmed.slice(0, -7) : trimmed;
}

export const TenantThemeProvider: React.FC<TenantThemeProviderProps> = ({
  children,
  tenantId: _tenantId,
  previewToken,
  apiBaseUrl = getApiOrigin(import.meta.env.VITE_API_URL),
}) => {
  const { t } = useTranslation();

  const [tenantTheme, setTenantTheme] = useState<TenantThemeSettings | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch tenant theme from API
   */
  const fetchTenantTheme = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await getAuthUser();
      const accessToken = user && !user.expired ? user.access_token : undefined;
      // Avoid noisy unauthenticated requests during auth bootstrap.
      if (!accessToken) {
        setTenantTheme(null);
        return;
      }
      // Roles the console refuses (D22) only get the portal page; the theme
      // is an admin API they may not read, so do not ask for it.
      if (!canUseConsole(rolesResultFor(getUserRoles(user)))) {
        setTenantTheme(null);
        return;
      }

      // Build API URL with optional preview token
      const normalizedApiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);
      let url = `${normalizedApiBaseUrl}/api/v1/admin/theme`;
      if (previewToken) {
        url += `?preview_token=${encodeURIComponent(previewToken)}`;
      }

      const response = await axios.get<TenantThemeSettings>(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setTenantTheme(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No tenant theme found - this is not an error, use global defaults
        setTenantTheme(null);
      } else {
        const errorMsg = err.response?.data?.detail || err.message;
        setError(errorMsg);
        console.error('Failed to fetch tenant theme:', errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, previewToken]);

  /**
   * Fetch theme on mount and when dependencies change
   */
  useEffect(() => {
    fetchTenantTheme();
  }, [fetchTenantTheme]);

  /**
   * Merged theme with tenant overrides applied to global defaults
   * Memoized for performance
   */
  const mergedTheme = useMemo<DesignTokens>(() => {
    const globalTokens = globalDesignTokens as DesignTokens;

    if (!tenantTheme) {
      return globalTokens;
    }

    // Extract only design token fields (exclude metadata)
    const tenantTokens: DesignTokens = {
      ...(tenantTheme.colors && { colors: tenantTheme.colors }),
      ...(tenantTheme.typography && { typography: tenantTheme.typography }),
      ...(tenantTheme.spacing && { spacing: tenantTheme.spacing }),
      ...(tenantTheme.borderRadius && { borderRadius: tenantTheme.borderRadius }),
      ...(tenantTheme.shadows && { shadows: tenantTheme.shadows }),
      ...(tenantTheme.animation && { animation: tenantTheme.animation }),
      ...(tenantTheme.glassmorphism && { glassmorphism: tenantTheme.glassmorphism }),
      ...(tenantTheme.components && { components: tenantTheme.components }),
      ...(tenantTheme.breakpoints && { breakpoints: tenantTheme.breakpoints }),
      ...(tenantTheme.zIndex && { zIndex: tenantTheme.zIndex }),
    };

    // Deep merge tenant overrides with global defaults
    return deepMerge(globalTokens, tenantTokens);
  }, [tenantTheme]);

  /**
   * Inject CSS custom properties into DOM
   * Debounced to avoid excessive DOM updates
   */
  const debouncedInjectCSS = useMemo(
    () =>
      debounce((theme: DesignTokens, customCss?: string) => {
        const cssProperties = generateCSSCustomProperties(theme);
        injectCustomProperties(cssProperties, customCss);
      }, 100),
    []
  );

  /**
   * Update CSS custom properties when theme changes
   */
  useEffect(() => {
    if (!isLoading) {
      debouncedInjectCSS(mergedTheme, tenantTheme?.custom_css);
    }
  }, [mergedTheme, tenantTheme?.custom_css, isLoading, debouncedInjectCSS]);

  /**
   * Context value
   */
  const contextValue: ThemeContextValue = useMemo(
    () => ({
      theme: mergedTheme,
      tenantTheme,
      isLoading,
      error,
      refetchTheme: fetchTenantTheme,
    }),
    [mergedTheme, tenantTheme, isLoading, error, fetchTenantTheme]
  );

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        {t('theme.loading', 'Loading theme...')}
      </div>
    );
  }

  // Show error state (non-blocking - fallback to global theme)
  if (error) {
    console.warn(`Theme loading error (using global defaults): ${error}`);
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default TenantThemeProvider;
